/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RuleCenterPage.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */


import React, { useEffect, useState, useCallback } from "react";
import { Box } from "@mui/material";
import { useDispatch } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import RuleSetFilters from "./RuleSetFilters";
import RuleSetTable from "./RuleSetTable";
import CommonBreadcrumbs from "../../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import CustomSearchbarWithApiV2 from "../../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import { HandleApiActions } from "../../../redux/actions";
import ruleEngineActions from "../../../redux/actions/ruleEngineAction";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";

function RuleCenterPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [ruleSets, setRuleSets] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const defaultFilters = {
    sortBy: "created_at",
    sortOrder: "DESC",
    status: "ALL",
    hasWorkflow: null,
  };

  const [filters, setFilters] = useState(defaultFilters); // Applied filters
  const [draftFilters, setDraftFilters] = useState(defaultFilters); // UI-only

  const [searchTerm, setSearchTerm] = useState("");

  const [openStandardsDialog, setOpenStandardsDialog] = useState(false);
  const [selectedStandards, setSelectedStandards] = useState([]);

  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const isFilterOpen = Boolean(filterAnchorEl);

  const mapRuleSetsToGridRows = (ruleSets = []) =>
    ruleSets?.map((rs, index) => ({
      id: rs.rule_set_id, // DataGrid row key (UUID)
      ruleSetName: rs.name || "-", // Column: Rule Set Name
      description: rs.description || "-", // Column: Description
      legalStandards: (rs.legal_standards || []).map((ls) => ({
        id: ls.ls_id,
        code: ls.standard_number,
        name: ls.name,
      })),
      legalStandardDisplay:
        (rs.legal_standards || []).map((ls) => ls.standard_number).join(", ") ||
        "-",
      created_by: rs?.created_by_user?.fullname || "-", // Column: Created By
      created_at: rs.created_at
        ? new Date(rs.created_at).toLocaleString()
        : "-", // Column: Created At
      _raw: rs, // Preserve full object for actions
    }));

  const fetchRuleSets = useCallback(
    async (override = {}) => {
      const params = {
        page: page,
        page_size: rowsPerPage,
        order_by_column: filters.sortBy,
        order_by_sort: filters.sortOrder,
        ...(filters.status !== "ALL" && { status: filters.status }),
        ...(filters.hasWorkflow !== null && {
          has_workflow: filters.hasWorkflow,
        }),
        ...override,
        ...(searchTerm && { name: searchTerm }),
      };

      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.get_rule_engine,
            params,
            show_toast: false,
          }),
        ),
      );

      if (resp?.error) {
        setRuleSets([]);
        setTotalCount(0);
      } else {
        const rows = mapRuleSetsToGridRows(resp?.data?.rule_sets);
        setRuleSets(rows);
        setTotalCount(resp?.data?.meta?.total_count || 0);
      }
    },
    [dispatch, page, rowsPerPage, filters, searchTerm],
  );

  useEffect(() => {
    fetchRuleSets();
  }, [fetchRuleSets]);

  const handleOpenFilters = (e) => {
    setDraftFilters(filters); // reset draft to applied state
    setFilterAnchorEl(e.currentTarget);
  };
  const handleCloseFilters = () => setFilterAnchorEl(null);

  const handleApplyFilters = () => {
    setFilters(draftFilters); // commit filters
    setPage(1);
    handleCloseFilters();
  };

  const handleClearFilters = () => {
    setDraftFilters(defaultFilters);
    setFilters(defaultFilters);
    setPage(1);
    handleCloseFilters();
  };

  const handleRuleCenterPageChange = (newPage) => {
    setPage(newPage);
  };

  const handleRuleCenterEdit = (ruleCenterRowItem) => {
    if (!ruleCenterRowItem) {
      return;
    }
    const selectedRuleSetIdForEdit = ruleCenterRowItem?._raw?.rule_set_id;
    if (!selectedRuleSetIdForEdit) {
      return;
    }
    navigate(`/dashboard/rule-center/${selectedRuleSetIdForEdit}/edit`);
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ px: 2, pt: 2 }}>
        <CommonBreadcrumbs
          items={[{ label: "Legal Requirements" }, { label: "Rule Center" }]}
        />
      </Box>

      <Box
        sx={{
          px: 2,
          py: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <RuleSetFilters
            anchorEl={filterAnchorEl}
            open={isFilterOpen}
            onOpen={handleOpenFilters}
            onClose={handleCloseFilters}
            filters={draftFilters}
            setFilters={setDraftFilters}
            onApply={handleApplyFilters}
            onClearAll={handleClearFilters}
          />

          <CustomSearchbarWithApiV2
            searchKey="name"
            placeholder="Search Rule Sets"
            size="small"
            onSearchQueryChange={(value) => {
              setSearchTerm(value);
              setPage(1); // reset page on new search
            }}
            getDataOnSearch={(value) => {
              console.log("value =", value);
            }}
          />
        </Box>

        <CustomButtonV2
          size="small"
          variant="contained"
          btnName="Create Rule"
          startIcon={<AddIcon />}
          handleOnClick={() => {
            navigate("/dashboard/rule-center/create");
          }}
        />
      </Box>

      <Box sx={{ flex: 1, px: 2, pb: 2 }}>
        <RuleSetTable
          rows={ruleSets}
          totalCount={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(page) => {
            handleRuleCenterPageChange(page);
          }}
          onPageSizeChange={setRowsPerPage}
          totalItems={totalCount}
          onEdit={(rowItem) => {
            handleRuleCenterEdit(rowItem);
          }}
          onViewStandards={(list) => {
            setSelectedStandards(list);
            setOpenStandardsDialog(true);
          }}
        />
        <CustomDialogBoxV2
          open={openStandardsDialog}
          onClose={() => setOpenStandardsDialog(false)}
          title={`Legal Standards (${selectedStandards.length})`}
          hideDefaultActions={true}
        >
          <Box sx={{ minWidth: 400 }}>
            {selectedStandards.map((ls) => (
              <Box
                key={ls.id}
                sx={{ mb: 2, pb: 1, borderBottom: "1px solid #eee" }}
              >
                <Box sx={{ fontWeight: 600 }}>{ls.code}</Box>
                <Box sx={{ fontSize: 13, color: "#666" }}>{ls.name}</Box>
              </Box>
            ))}
          </Box>
        </CustomDialogBoxV2>
      </Box>
    </Box>
  );
}

export default RuleCenterPage;
